import { faCircleCheck, faCircleXmark, faPenToSquare, faPlus, faSliders } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Yup from 'yup';
import { Input, Form, Result } from 'antd';
import Loader from 'Common/Loader';
import DataGrid, { Column, Pager, Paging, SearchPanel, Summary, TotalItem } from 'devextreme-react/cjs/data-grid';
import React, { useEffect, useRef, useState } from 'react';
import { Card, Modal } from 'react-bootstrap';
import { createRole, getAllUserRoles, getRolesById, updateAllUserPermissionByRoleId, updateRole, updateRolePermission } from '../services';
import CustomStore from 'devextreme/data/custom_store';
import { useFormik } from 'formik';
import RolesPermission from './RolesPermission';

const defaultData = {
    id: null,
    name: ""
};

const validationSchema = Yup.object({
    name: Yup.string().required('Role Name is required'),
});

const defaultRoleData = {
    roleId: null,
    roleName: ''
}

const RoleMasterMain = () => {
    const [loading, setLoading] = useState(false);
    const dataGridRef = useRef<any>(null);
    const [showAddRoleModal, setShowAddRoleModal] = useState(false);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [permissionDetails, setPermissionDetails] = useState<string[]>([]);
    const [selectedRoleId, setSelectedRoleId] = useState(defaultRoleData);
    const [pagination, setPagination] = useState({ pageSize: 10, pageNumber: 1, });

    useEffect(() => {
        const authUserString = localStorage.getItem("authPOSUser");
        if (authUserString) {
            const authUser = JSON.parse(authUserString);
            const permissionData = authUser?.permissions || [];
            setPermissionDetails(permissionData);
        }
    }, []);

    const HasPermission = (permission: string): boolean => {
        return permissionDetails.includes(permission);
    };

    const dataSource = React.useMemo(() => {
        if (showAddRoleModal) return null;
        return new CustomStore({
            key: 'id',
            load: async (loadOptions) => {
                try {
                    const params = new URLSearchParams();
                    params.set('skip', String(loadOptions.skip ?? 0));
                    params.set('take', String(loadOptions.take ?? 10 * pagination.pageNumber));
                    params.set('sort', JSON.stringify(loadOptions.sort ?? []));
                    params.set('filter', JSON.stringify(loadOptions.filter ?? null));
                    params.set('searchExpr', JSON.stringify(loadOptions.searchExpr ?? null));
                    params.set('searchOperation', loadOptions.searchOperation ?? '');
                    params.set('searchValue', loadOptions.searchValue ?? '');
                    const response = await getAllUserRoles(params);

                    if (!response?.data || response.data.length === 0) {
                        return { data: [], totalCount: 0 };
                    }
                    return response.data.reverse();
                } catch (error) {
                    console.error("Data loading error:", error);
                    return { data: [], totalCount: 0 };
                }
            },
        });
    }, [showAddRoleModal]);

    const onPagingChange = ({ pageSize, pageIndex, }: { pageSize?: number; pageIndex?: number; }) => {
        setPagination({
            pageSize: pageSize ?? pagination.pageSize,
            pageNumber: (pageIndex ?? pagination.pageNumber - 1) + 1,
        });
    };

    const handleUpdateRole = async (userData: any) => {
        try {
            setLoading(true);
            const response = await getRolesById(userData?.id);
            const detailedUser = response?.data;
            // console.log(response);
            if (detailedUser) {
                formik.setValues({
                    id: detailedUser?.id || null,
                    name: detailedUser?.name || "",
                });
                setShowAddRoleModal(true);
            }
        } catch (error) {
            console.error('Failed to fetch role details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPermissionComponent = (data: any) => {
        setSelectedRoleId({
            roleId: data?.id,
            roleName: data?.name
        });
        setShowPermissionModal(true);
    }

    const formik = useFormik({
        initialValues: defaultData,
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setLoading(true);
                const payload = { ...values };
                if (payload?.id) {
                    await updateRole(payload);
                    console.log("role updated successfully");
                } else {
                    const { id, ...createRolePayload } = values;
                    await createRole(createRolePayload);
                    console.log("role created successfully");
                }
                formik.resetForm();
                setShowAddRoleModal(false);
                dataGridRef?.current?.instance?.refresh();
            } catch (error: any) {
                console.error("API Error:", error.message);
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        },
    });

    const handleSubmitRolesPermission = async (data: any) => {
        const PermissionData = {
            roleId: selectedRoleId?.roleId,
            permissionIds: data
        }
        const formData = new FormData();
        if (selectedRoleId?.roleId != null) {
            formData.append('roleId', String(selectedRoleId.roleId));
        } else {
            console.error("roleId is null or undefined. Cannot send to API.");
            return;
        }
        try {
            setLoading(true);
            await updateRolePermission(PermissionData);
            await updateAllUserPermissionByRoleId(formData);
            setSelectedRoleId(defaultRoleData);
            console.log("Permission updated successfully");
            setShowPermissionModal(false);
        } catch (error: any) {
            console.error("API Error:", error.message);
        } finally {
            setLoading(false);
        }
    }

    const handleOpen = () => setShowAddRoleModal(true);
    const handleClose = () => {
        formik.resetForm();
        setShowAddRoleModal(false);
    };

    return (
        <React.Fragment>
            {loading && <Loader />}
            <div className="page-content">
                <div className="container-fluid">
                    <Card>
                        <Card.Header className='d-flex justify-content-between align-items-center'>
                            <h5 className='m-0'>Role Master</h5>
                            {/* {HasPermission("UserRole.Write") && <button className="add-btn" onClick={handleOpen}>
                                <FontAwesomeIcon icon={faPlus} className='ml-2' /> Add Role
                            </button>} */}
                        </Card.Header>
                        <Card.Body className="pt-0">
                            {HasPermission("UserRole.Read") ?
                                <DataGrid
                                    ref={dataGridRef}
                                    dataSource={dataSource}
                                    remoteOperations={{ paging: true, filtering: true, sorting: true }}
                                    onOptionChanged={(e) => {
                                        if (e.fullName === 'paging.pageIndex' || e.fullName === 'paging.pageSize') {
                                            onPagingChange({
                                                pageIndex: e.fullName === 'paging.pageIndex' ? e.value : pagination.pageNumber - 1,
                                                pageSize: e.fullName === 'paging.pageSize' ? e.value : pagination.pageSize,
                                            });
                                        }
                                    }}
                                    showRowLines={true}
                                    keyExpr="id"
                                    showBorders={true}
                                    onContentReady={(e) => {
                                        const hasData = e.component.totalCount() > 0;
                                        e.component.option("pager.visible", hasData);
                                        e.component.option("paging.visible", hasData);
                                    }}
                                >
                                    <Pager
                                        showPageSizeSelector={true}
                                        allowedPageSizes={[10, 25, 50, 100]}
                                        showNavigationButtons={true}
                                        displayMode="full"
                                        showInfo={true}
                                    />
                                    <Summary>
                                        <TotalItem
                                        // column="id"
                                        // summaryType="count"
                                        // displayFormat="Total Roles: {0}"
                                        />
                                    </Summary>
                                    <Paging pageSize={pagination?.pageSize} />
                                    <SearchPanel visible width={240} placeholder="Search..." />

                                    {HasPermission("UserRole.Write") && <Column
                                        caption="Action"
                                        width="15%"
                                        cssClass="text-center"
                                        cellRender={({ data }) => (
                                            <div className="d-flex justify-content-center align-items-center gap-2">
                                                {/* {HasPermission("UserRole.Write") && <button className='btn btn-sm action-btn edit-btn' onClick={() => handleUpdateRole(data)}><FontAwesomeIcon icon={faPenToSquare} className='ml-2' /></button>} */}
                                                {/* <button className='btn btn-sm action-btn delete-btn' onClick={() => { handleDeleteUser(data?.id) }}><FontAwesomeIcon icon={faTrash} className='ml-2' /></button> */}
                                                {HasPermission("Discount.Write") && <button className='btn btn-sm action-btn permission-btn' onClick={() => handleOpenPermissionComponent(data)}><FontAwesomeIcon icon={faSliders} className='ml-2' title='Update Permissions' /></button>}
                                            </div>
                                        )}
                                    />}
                                    {/* <Column dataField="id" caption="Id" dataType="string" width="15%" /> */}
                                    <Column dataField="name" caption="Name" dataType="string" />
                                    <Column
                                        dataField="active"
                                        caption="Active"
                                        dataType="string"
                                        width="20%"
                                        cssClass="text-center"
                                        cellRender={({ data }) => (
                                            <>
                                                {data?.active ? <FontAwesomeIcon className='text-success' icon={faCircleCheck} /> : <FontAwesomeIcon icon={faCircleXmark} className='text-danger' />}
                                            </>
                                        )}
                                    />

                                </DataGrid>
                                :
                                null
                            }
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <Modal show={showAddRoleModal} onHide={handleClose} className="fade zoomIn" dialogClassName="modal-dialog-centered" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {formik.values.id ? 'Update Role Datails' : 'Add New Role Details'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form layout="vertical">
                        <div className="row">
                            <div className="col-md-12">
                                <Form.Item label={
                                    <>
                                        Role Name <span style={{ color: 'red' }}>*</span>
                                    </>
                                } className='mb-2'>
                                    <Input name="name" placeholder="Enter name" value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    {formik.touched.name && formik.errors.name && <div className="text-danger">{formik.errors.name}</div>}
                                </Form.Item>
                            </div>
                        </div>
                    </Form>
                    <div className="d-flex gap-2 justify-content-end mt-4 mb-2">
                        <button className="add-btn" onClick={handleClose}>
                            Cancel
                        </button>
                        <button className="submit-btn" onClick={() => formik.handleSubmit()}>
                            {formik.values.id ? 'Update Role' : 'Add Role'}
                        </button>
                    </div>
                </Modal.Body>
            </Modal>



            {/* Roles and Permission Component */}
            <RolesPermission
                show={showPermissionModal}
                onClose={() => {
                    setShowPermissionModal(false);
                    setSelectedRoleId({
                        roleId: null,
                        roleName: ''
                    })
                }}
                onSave={(updatedPermissions) => {
                    handleSubmitRolesPermission(updatedPermissions);
                }}
                roleId={selectedRoleId?.roleId}
                roleName={selectedRoleId?.roleName}
                flag="Role"
            />
        </React.Fragment >

    );
};

export default RoleMasterMain;
